import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputBase } from '@mui/material';
import { useEffect, useState } from 'react';

import { FILTER_CONTROL_SX } from '~/theme';

/**
 * How long typing has to pause before the search runs. This input drives
 * `filterModel.quickFilterValues`, and on Spending and Tickets that model is the SWR key
 * for a server-side query — so an undebounced `onChange` was one request per keystroke.
 */
export const QUICK_FILTER_DEBOUNCE_MS = 300;

export interface QuickFilterProps {
  searchValue: string;
  placeholder?: string;
  handleSearchValueChange: (value: string) => void;
  /**
   * Bumped by Clear Filters. A clear is a command, not a new value: with a term still inside
   * the debounce `searchValue` is already '', so nothing below would notice and the box kept
   * the text and re-ran the search a moment later.
   */
  clearToken?: number;
}

export const QuickFilterInput = ({
  searchValue,
  handleSearchValueChange,
  placeholder,
  clearToken = 0,
}: QuickFilterProps) => {
  // The box tracks its own text so typing stays responsive, while the value the
  // table filters on lags behind it by the debounce.
  const [draft, setDraft] = useState(searchValue);
  const [adopted, setAdopted] = useState(searchValue);
  const [clearedAt, setClearedAt] = useState(clearToken);

  if (clearToken !== clearedAt) {
    setClearedAt(clearToken);
    setAdopted(searchValue);
    setDraft(searchValue);
  }

  // Adopt a value that changed from the outside — a Clear Filters press, or a filter
  // model restored from session storage. Adjusting during render rather than in an
  // effect, which would paint the stale text first.
  if (searchValue !== adopted) {
    setAdopted(searchValue);
    setDraft(searchValue);
  }

  useEffect(() => {
    // Already in sync — either nothing was typed, or this render is the echo of
    // our own commit coming back through props.
    if (draft === searchValue) return;

    const timer = setTimeout(() => handleSearchValueChange(draft), QUICK_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, searchValue, handleSearchValueChange]);

  return (
    <Box
      component="form"
      display="flex"
      alignItems="center"
      onSubmit={(event) => event.preventDefault()}
      sx={{
        // No active tint here, unlike the dropdowns: the typed text is already
        // the signal, and blue-on-blue input text reads as disabled.
        ...FILTER_CONTROL_SX,
        pl: 1,
        pr: 0.5,
        width: { xs: '100%', sm: 'auto' },
        minWidth: { sm: 200 },
      }}
    >
      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      <InputBase
        sx={{ ml: 1, flex: 1, minWidth: 0, fontSize: 14 }}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
      />
      {draft ? (
        <IconButton size="small" aria-label="Clear search" onClick={() => setDraft('')}>
          <ClearIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
};
